/**
 * PatientImport page — /patients/import
 *
 * Three tabs:
 *   1. Quick Entry  — manual grid for up to 20 rows
 *   2. File Import  — CSV / XLSX upload + preview
 *   3. Need Help?   — support request form for custom formats
 */
import React, {useContext} from 'react';
import {Alert, Button, Col, Divider, Row, Space, Tabs, Typography} from 'antd';
import {DownloadOutlined, HistoryOutlined} from '@ant-design/icons';
import {Navigate, useNavigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {QuickEntryGrid, GridRow} from '../components/patientImport/QuickEntryGrid';
import {FileImportPreview} from '../components/patientImport/FileImportPreview';
import {SupportRequestForm} from '../components/patientImport/SupportRequestForm';
import DetailWrapper from '../components/common/DetailWrapper';
import {DentobookApplicationContext} from '../context/DentobookApplicationContext';
import {useHelpSection} from '../context/HelpSectionContext';
import {getPatientImportApi} from '../api/apiInstances';
import type {CommitResponse, ImportRowInput} from '../api-client';
import {useState} from 'react';
import {useTrackEvent} from '../hooks/useTrackEvent';

const patientImportApi = getPatientImportApi();

const {Paragraph} = Typography;

const ALLOWED_ROLES = ['CLINIC_ADMIN'];

/**
 * Normalise a user-typed date string to YYYY-MM-DD for the API.
 * Accepts:  YYYY-MM-DD (passthrough), DD.MM.YYYY, DD/MM/YYYY
 * Anything else is forwarded as-is so the server can return a meaningful error.
 */
function normalizeDateInput(s: string | null): string | null {
    if (!s) return null;
    const v = s.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const dot = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dot) return `${dot[3]}-${dot[2].padStart(2, '0')}-${dot[1].padStart(2, '0')}`;
    const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slash) return `${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}`;
    return v;
}

function gridRowToInput(row: GridRow): ImportRowInput {
    return {
        rowIndex: row.rowIndex,
        firstName: row.firstName || null,
        secondName: row.secondName || null,
        lastName: row.lastName || null,
        email: row.email || null,
        phone: row.phone || null,
        birthDate: normalizeDateInput(row.birthDate),
        gender: row.gender || null,
        nationalId: row.nationalId || null,
        notes: row.notes || null,
    };
}

const EMPTY_ROWS: GridRow[] = [
    {
        key: 'row-0-init',
        rowIndex: 0,
        firstName: '',
        lastName: '',
        secondName: '',
        email: '',
        phone: '',
        birthDate: '',
        gender: '',
        nationalId: '',
        notes: '',
    },
];

/** Trigger a browser file download from a blob response. */
function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

const QuickEntryTab: React.FC = () => {
    const intl = useIntl();
    const {userOrganization} = useContext(DentobookApplicationContext);
    const [rows, setRows] = useState<GridRow[]>(EMPTY_ROWS);
    const [validating, setValidating] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [commitResult, setCommitResult] = useState<CommitResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const track = useTrackEvent();

    const nonEmptyRows = rows.filter(r =>
        r.firstName || r.lastName || r.secondName ||
        r.email || r.phone || r.birthDate ||
        r.gender || r.nationalId || r.notes
    );

    const handleValidate = async () => {
        if (nonEmptyRows.length === 0) return;
        setError(null);
        setValidating(true);
        try {
            const resp = await patientImportApi.validateImportRows(
                {rows: nonEmptyRows.map(gridRowToInput)}
            );
            track('patient_import_started', { import_method: 'quick_entry', row_count: nonEmptyRows.length });
            const resultMap = new Map(
                resp.data.rows.map(r => [r.rowIndex, r])
            );
            setRows(prev =>
                prev.map(row => {
                    const result = resultMap.get(row.rowIndex);
                    if (!result) return row;
                    return {
                        ...row,
                        status: result.status as GridRow['status'],
                        validationErrors: result.validationErrors ?? undefined,
                        duplicateInfo: result.duplicateInfo?.map(d => ({
                            type: (d as any).type,
                            name: (d as any).name ?? undefined,
                            patientId: (d as any).patientId ?? undefined,
                        })) ?? undefined,
                        firstName: result.firstName ?? row.firstName,
                        lastName: result.lastName ?? row.lastName,
                        secondName: result.secondName ?? row.secondName,
                        email: result.email ?? row.email,
                        phone: result.phone ?? row.phone,
                        birthDate: result.birthDate ?? row.birthDate,
                        gender: result.gender ?? row.gender,
                        nationalId: result.nationalId ?? row.nationalId,
                        notes: result.notes ?? row.notes,
                    };
                })
            );
        } catch (e: any) {
            setError(e?.response?.data?.detail || intl.formatMessage({id: 'import_validate_error'}));
        } finally {
            setValidating(false);
        }
    };

    const handleCommit = async () => {
        if (nonEmptyRows.length === 0) return;
        setError(null);
        setCommitting(true);
        try {
            const resp = await patientImportApi.commitManualImport(
                {rows: nonEmptyRows.map(gridRowToInput)}
            );
            setCommitResult(resp.data);
            const resultMap = new Map(resp.data.rows.map(r => [r.rowIndex, r]));
            setRows(prev =>
                prev.map(row => {
                    const result = resultMap.get(row.rowIndex);
                    if (!result) return row;
                    return {
                        ...row,
                        status: result.status as GridRow['status'],
                        validationErrors: result.validationErrors ?? undefined,
                        duplicateInfo: result.duplicateInfo?.map(d => ({
                            type: (d as any).type,
                            name: (d as any).name ?? undefined,
                            patientId: (d as any).patientId ?? undefined,
                        })) ?? undefined,
                    };
                })
            );
        } catch (e: any) {
            setError(e?.response?.data?.detail || intl.formatMessage({id: 'import_commit_error'}));
        } finally {
            setCommitting(false);
        }
    };

    const committableCount = rows.filter(
        r => r.status === 'valid' || r.status === 'duplicate_warning'
    ).length;

    return (
        <div>
            <Paragraph type="secondary" style={{marginBottom: 16}}>
                <FormattedMessage
                    id="import_quick_entry_hint"
                    values={{
                        ctrl: <strong>Ctrl+V</strong>,
                        enter: <strong>Enter</strong>,
                        tab: <strong>Tab</strong>,
                        shiftTab: <strong>Shift + Tab</strong>,
                        addRow: <strong>+ Add row</strong>,
                        br: <br/>,
                    }}
                />
            </Paragraph>

            {error && (
                <Alert
                    type="error"
                    message={error}
                    closable
                    onClose={() => setError(null)}
                    style={{marginBottom: 12}}
                />
            )}

            {commitResult && (
                <Alert
                    type={commitResult.failed > 0 ? 'warning' : 'success'}
                    message={
                        <FormattedMessage
                            id="import_commit_result"
                            values={{
                                committed: commitResult.committed,
                                failed: commitResult.failed,
                                skipped: commitResult.skipped,
                            }}
                        />
                    }
                    style={{marginBottom: 12}}
                    closable
                    onClose={() => setCommitResult(null)}
                />
            )}

            <QuickEntryGrid
                rows={rows}
                onChange={r => {
                    setRows(r);
                    setCommitResult(null);
                }}
                validating={validating || committing}
                country={userOrganization?.country ?? undefined}
            />

            <Divider style={{margin: '12px 0'}}/>

            <Space>
                <Button
                    onClick={handleValidate}
                    loading={validating}
                    disabled={nonEmptyRows.length === 0 || committing}
                >
                    <FormattedMessage id="import_validate_button"/>
                </Button>

                <Button
                    type="primary"
                    onClick={handleCommit}
                    loading={committing}
                    disabled={nonEmptyRows.length === 0 || validating}
                >
                    {committableCount > 0 ? (
                        <FormattedMessage
                            id="import_commit_button"
                            values={{count: committableCount}}
                        />
                    ) : (
                        <FormattedMessage id="import_commit_all_button"/>
                    )}
                </Button>

                <Button
                    danger
                    onClick={() => {
                        setRows(EMPTY_ROWS);
                        setCommitResult(null);
                        setError(null);
                    }}
                    disabled={validating || committing}
                >
                    <FormattedMessage id="import_clear_all"/>
                </Button>
            </Space>
        </div>
    );
};

const PatientImport: React.FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const {principal, userOrganization} = useContext(DentobookApplicationContext);
    useHelpSection('patient-import');

    if (principal && !ALLOWED_ROLES.includes(principal.user?.role ?? '')) {
        return <Navigate to="/patients" replace/>;
    }

    const handleDownloadCsv = async () => {
        // responseType: 'blob' is required — without it Axios parses the binary
        // response as a string, which is not a Blob and crashes createObjectURL.
        const resp = await patientImportApi.downloadImportTemplateCsv({responseType: 'blob'});
        const blob = new Blob([resp.data as BlobPart], {type: 'text/csv'});
        triggerDownload(blob, 'dentobook-patient-import-template.csv');
    };

    const handleDownloadXlsx = async () => {
        const resp = await patientImportApi.downloadImportTemplateXlsx({responseType: 'blob'});
        const blob = new Blob([resp.data as BlobPart], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        triggerDownload(blob, 'dentobook-patient-import-template.xlsx');
    };

    const tabItems = [
        {
            key: 'quick',
            label: intl.formatMessage({id: 'import_tab_quick_entry'}),
            children: <QuickEntryTab/>,
        },
        {
            key: 'file',
            label: intl.formatMessage({id: 'import_tab_file'}),
            children: <FileImportPreview/>,
        },
        {
            key: 'support',
            label: intl.formatMessage({id: 'import_tab_support'}),
            children: <SupportRequestForm/>,
        },
    ];

    return (
        <DetailWrapper>
            <Row className="mb-2">
                <Col span={12}>
                    <h3><FormattedMessage id="import_patients"/></h3>
                </Col>
                <Col span={12}>
                    <div style={{float: 'right', marginTop: '24px'}}>
                        <Space>
                            <Button className="rounded-btn" onClick={handleDownloadCsv}>
                                <DownloadOutlined/>{' '}<FormattedMessage id="import_download_template_csv"/>
                            </Button>
                            <Button className="rounded-btn" onClick={handleDownloadXlsx}>
                                <DownloadOutlined/>{' '}<FormattedMessage id="import_download_template_xlsx"/>
                            </Button>
                            <Button className="rounded-btn" onClick={() => navigate('/patients/import/history')}>
                                <HistoryOutlined/>{' '}<FormattedMessage id="import_view_history"/>
                            </Button>
                        </Space>
                    </div>
                </Col>
            </Row>

            <Paragraph type="secondary" style={{marginBottom: 16}}>
                <FormattedMessage id="import_patients_desc"/>
            </Paragraph>

            <Alert
                type="info"
                showIcon
                message={<FormattedMessage id="import_v1_scope_notice"/>}
                style={{marginBottom: 20}}
            />

            <Tabs items={tabItems} defaultActiveKey="quick"/>
        </DetailWrapper>
    );
};

export default PatientImport;
